import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, Shield, Phone, CheckCircle, Star, User, Heart, Activity, Home, Camera, Watch } from 'lucide-react';
import ImageService from '../services/ImageService';

const ElderlyServiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  // Service data - in a real app, this would come from an API
  const services = {
    1: {
      id: 1,
      name: 'Basic Health Checks',
      category: 'health',
      description: 'Routine monitoring of vital signs and overall well-being',
      fullDescription: 'Our Basic Health Checks service provides comprehensive health monitoring for elderly individuals in the comfort of their own homes. Our trained healthcare professionals conduct regular assessments to ensure early detection of health issues and maintain optimal wellness.',
      image: ImageService.getImageUrl('health-check.jpg'),
      icon: Activity,
      price: '$85/visit',
      duration: '45-60 minutes',
      frequency: 'Weekly or as needed',
      features: [
        'Blood pressure monitoring',
        'Temperature checks',
        'Pulse rate monitoring',
        'Weight tracking',
        'Medication reminders',
        'Health status reporting'
      ],
      benefits: [
        'Early detection of health issues',
        'Peace of mind for families',
        'Professional health monitoring',
        'Detailed health reports',
        'Medication compliance support',
        'Emergency alert system'
      ],
      process: [
        'Initial health assessment and care plan development',
        'Scheduled regular visits by certified healthcare professionals',
        'Comprehensive vital signs monitoring and documentation',
        'Medication review and reminder setup',
        'Family communication and health status updates',
        'Emergency response coordination if needed'
      ],
      testimonials: [
        {
          name: 'Sarah Johnson',
          rating: 5,
          comment: 'The health check service has been invaluable for monitoring my mother\'s condition. The staff is professional and caring.'
        },
        {
          name: 'Michael Chen',
          rating: 5,
          comment: 'Regular health monitoring gives us peace of mind knowing dad is well cared for at home.'
        }
      ]
    },
    2: {
      id: 2,
      name: 'Preventive Home Maintenance',
      category: 'maintenance',
      description: 'Visual inspection and upkeep to ensure a safe living environment',
      fullDescription: 'Our Preventive Home Maintenance service focuses on creating and maintaining a safe living environment for elderly residents. We conduct thorough safety inspections and perform necessary maintenance to prevent accidents and ensure comfort.',
      image: ImageService.getImageUrl('home-maintenance.jpg'),
      icon: Home,
      price: '$120/visit',
      duration: '2-3 hours',
      frequency: 'Monthly',
      features: [
        'Safety hazard inspection',
        'Handrail stability checks',
        'Lighting assessment',
        'Floor safety evaluation',
        'Emergency exit clearance',
        'Basic repair identification'
      ],
      benefits: [
        'Accident prevention',
        'Safe living environment',
        'Improved mobility and independence',
        'Family peace of mind',
        'Professional safety assessment',
        'Maintenance recommendations'
      ],
      process: [
        'Comprehensive home safety assessment',
        'Identification of potential hazards and risks',
        'Priority repair and maintenance recommendations',
        'Implementation of safety improvements',
        'Regular follow-up inspections',
        'Emergency repair coordination'
      ],
      testimonials: [
        {
          name: 'Robert Davis',
          rating: 5,
          comment: 'The home maintenance team identified several safety issues we hadn\'t noticed. Very thorough and professional.'
        }
      ]
    },
    3: {
      id: 3,
      name: 'Daily Check-In Call',
      category: 'communication',
      description: 'Phone or WhatsApp contact for reassurance and immediate updates',
      fullDescription: 'Our Daily Check-In Call service provides regular communication and wellness checks through phone or WhatsApp. This service ensures daily contact, emotional support, and immediate assistance when needed.',
      image: ImageService.getImageUrl('phone-call.jpg'),
      icon: Phone,
      price: '$45/month',
      duration: '10-15 minutes per call',
      frequency: 'Daily',
      features: [
        'Morning wellness check',
        'Evening safety call',
        'Emergency contact alerts',
        'Family updates',
        'Medication reminders',
        'Emotional support'
      ],
      benefits: [
        'Daily human connection',
        'Early problem detection',
        'Family communication',
        'Emergency response',
        'Emotional wellbeing support',
        'Medication compliance'
      ],
      process: [
        'Establish preferred communication times and methods',
        'Daily wellness and safety check calls',
        'Documentation of health and wellbeing status',
        'Emergency contact notification when needed',
        'Weekly family updates and reports',
        '24/7 emergency response coordination'
      ],
      testimonials: [
        {
          name: 'Linda Martinez',
          rating: 5,
          comment: 'The daily calls have been a lifeline for my grandmother. She looks forward to them every day.'
        }
      ]
    },
    4: {
      id: 4,
      name: 'Grocery Restock',
      category: 'delivery',
      description: 'Delivery of essentials to maintain daily convenience',
      fullDescription: 'Our Grocery Restock service ensures that elderly individuals have access to fresh groceries, medications, and household essentials without the need to leave their homes. We provide reliable delivery and shopping assistance.',
      image: ImageService.getImageUrl('grocery-delivery.jpg'),
      icon: Shield,
      price: '$35/delivery + groceries',
      duration: '1-2 hours',
      frequency: 'Weekly or as needed',
      features: [
        'Weekly grocery delivery',
        'Pharmacy pickups',
        'Fresh produce delivery',
        'Household essentials',
        'Special dietary items',
        'Emergency supply runs'
      ],
      benefits: [
        'Convenience and independence',
        'Fresh, quality groceries',
        'Medication management',
        'Time and energy savings',
        'Customized shopping lists',
        'Emergency supply access'
      ],
      process: [
        'Initial consultation to understand preferences and needs',
        'Weekly shopping list creation and approval',
        'Professional grocery shopping and selection',
        'Timely delivery and proper storage',
        'Medication pickup and delivery coordination',
        'Emergency delivery service when needed'
      ],
      testimonials: [
        {
          name: 'Thomas Wilson',
          rating: 5,
          comment: 'The grocery service has made such a difference for my father. Always fresh food and exactly what he needs.'
        }
      ]
    },
    5: {
      id: 5,
      name: 'Housekeeping',
      category: 'cleaning',
      description: 'Includes laundry, sweeping, mopping, light cleaning, and linen change',
      fullDescription: 'Our comprehensive Housekeeping service maintains a clean, comfortable, and healthy living environment for elderly residents. We provide regular cleaning, laundry, and home organization services.',
      image: ImageService.getImageUrl('housekeeping.jpg'),
      icon: Home,
      price: '$95/visit',
      duration: '2-4 hours',
      frequency: 'Weekly or bi-weekly',
      features: [
        'Laundry and folding',
        'Floor sweeping and mopping',
        'Light dusting and cleaning',
        'Bed linen changes',
        'Bathroom cleaning',
        'Kitchen maintenance'
      ],
      benefits: [
        'Clean and healthy environment',
        'Reduced allergens and bacteria',
        'Improved quality of life',
        'Professional cleaning standards',
        'Time and energy savings',
        'Organized living space'
      ],
      process: [
        'Initial home assessment and cleaning plan',
        'Scheduled regular cleaning visits',
        'Comprehensive room-by-room cleaning',
        'Laundry and linen management',
        'Organization and decluttering assistance',
        'Special cleaning requests accommodation'
      ],
      testimonials: [
        {
          name: 'Patricia Brown',
          rating: 5,
          comment: 'The housekeeping team is wonderful. They keep my mother\'s home spotless and she feels so much better.'
        }
      ]
    },
    6: {
      id: 6,
      name: '24-Hour AI Camera',
      category: 'technology',
      description: 'Privacy-compliant monitoring to ensure safety at home',
      fullDescription: 'Our 24-Hour AI Camera system provides advanced, privacy-compliant monitoring to ensure the safety and security of elderly residents. Using cutting-edge technology, we detect emergencies and unusual activities while respecting privacy.',
      image: ImageService.getImageUrl('ai-camera.jpg'),
      icon: Camera,
      price: '$150/month',
      duration: '24/7 monitoring',
      frequency: 'Continuous',
      features: [
        'Fall detection monitoring',
        'Unusual activity alerts',
        'Emergency response triggers',
        'Privacy-focused recording',
        'Family notification system',
        '24/7 monitoring service'
      ],
      benefits: [
        'Immediate emergency detection',
        'Privacy protection',
        'Family peace of mind',
        'Professional monitoring',
        'Rapid emergency response',
        'Activity pattern analysis'
      ],
      process: [
        'Professional camera installation and setup',
        'Privacy settings configuration and consent',
        'AI system training and calibration',
        '24/7 monitoring center activation',
        'Emergency response protocol establishment',
        'Regular system maintenance and updates'
      ],
      testimonials: [
        {
          name: 'Jennifer Lee',
          rating: 5,
          comment: 'The AI camera system detected when my dad fell and help arrived within minutes. It saved his life.'
        }
      ]
    },
    7: {
      id: 7,
      name: 'Fitbit Emergency Response',
      category: 'technology',
      description: 'Wearable device to detect falls or emergencies',
      fullDescription: 'Our Fitbit Emergency Response service provides advanced wearable technology to detect falls, monitor health metrics, and provide immediate emergency assistance. The device offers peace of mind through continuous monitoring.',
      image: ImageService.getImageUrl('fitbit-emergency.jpg'),
      icon: Watch,
      price: '$89/month',
      duration: '24/7 monitoring',
      frequency: 'Continuous',
      features: [
        'Fall detection alerts',
        'Heart rate monitoring',
        'Emergency button activation',
        'GPS location tracking',
        'Automatic emergency calls',
        'Health data tracking'
      ],
      benefits: [
        'Immediate emergency response',
        'Health monitoring insights',
        'Location tracking for safety',
        'Family connectivity',
        'Professional emergency support',
        'Health trend analysis'
      ],
      process: [
        'Device setup and personalization',
        'Emergency contact configuration',
        'Health baseline establishment',
        'Monitoring service activation',
        'Family app access setup',
        'Regular device maintenance and updates'
      ],
      testimonials: [
        {
          name: 'David Kim',
          rating: 5,
          comment: 'The Fitbit device gives us confidence that help is always available. The health tracking is an added bonus.'
        }
      ]
    }
  };

  const service = services[id];

  if (!service) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Service Not Found</h2>
          <Link to="/elderly-care" className="text-orange-600 hover:text-orange-700">
            ← Back to Elderly Care Services
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = service.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <button
            onClick={() => navigate('/elderly-care')}
            className="flex items-center text-orange-600 hover:text-orange-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Elderly Care Services
          </button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <IconComponent className="w-8 h-8 text-orange-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{service.name}</h1>
                <p className="text-gray-600">{service.description}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-orange-600">{service.price}</div>
              <div className="text-sm text-gray-500">{service.frequency}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Image */}
      <div className="h-64 bg-gray-200 overflow-hidden">
        <img 
          src={service.image} 
          alt={service.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        <div className="w-full h-full bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center" style={{display: 'none'}}>
          <IconComponent className="w-24 h-24 text-orange-600" />
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-md mb-6">
              <div className="border-b">
                <nav className="flex space-x-8 px-6">
                  {[
                    { id: 'overview', label: 'Overview' },
                    { id: 'features', label: 'Features' },
                    { id: 'process', label: 'Process' },
                    { id: 'testimonials', label: 'Reviews' }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === tab.id
                          ? 'border-orange-500 text-orange-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Service Overview</h3>
                    <p className="text-gray-700 mb-6 leading-relaxed">{service.fullDescription}</p>
                    
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Key Benefits</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {service.benefits.map((benefit, index) => (
                        <div key={index} className="flex items-center">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                          <span className="text-gray-700">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Features Tab */}
                {activeTab === 'features' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Service Features</h3>
                    <div className="space-y-4">
                      {service.features.map((feature, index) => (
                        <div key={index} className="flex items-start">
                          <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                          <div>
                            <h5 className="font-medium text-gray-900">{feature}</h5>
                            <p className="text-gray-600 text-sm mt-1">
                              Professional implementation of {feature.toLowerCase()} as part of our comprehensive care service.
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Process Tab */}
                {activeTab === 'process' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Our Process</h3>
                    <div className="space-y-6">
                      {service.process.map((step, index) => (
                        <div key={index} className="flex items-start">
                          <div className="flex-shrink-0 w-8 h-8 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center font-semibold text-sm mr-4">
                            {index + 1}
                          </div>
                          <div>
                            <p className="text-gray-700">{step}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Testimonials Tab */}
                {activeTab === 'testimonials' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Client Reviews</h3>
                    <div className="space-y-6">
                      {service.testimonials.map((testimonial, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center mb-2">
                            <User className="w-8 h-8 text-gray-400 mr-3" />
                            <div>
                              <h5 className="font-medium text-gray-900">{testimonial.name}</h5>
                              <div className="flex items-center">
                                {[...Array(testimonial.rating)].map((_, i) => (
                                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                                ))}
                              </div>
                            </div>
                          </div>
                          <p className="text-gray-700 italic">"{testimonial.comment}"</p>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>
            </div>

            {/* Book Service Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Book This Service</h3>
              <p className="text-gray-600 mb-6">
                Ready to provide exceptional care for your loved one? Book this service now to get started or contact us for a personalized consultation.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <Link
                  to={`/elderly-order-request?service=${service.id}&title=${encodeURIComponent(service.name)}&category=elderly-care`}
                  className="block w-full bg-orange-600 text-white text-center py-4 rounded-lg hover:bg-orange-700 transition-colors font-semibold text-lg"
                >
                  Book Now
                </Link>
                <Link
                  to="/contact"
                  className="block w-full border-2 border-orange-600 text-orange-600 text-center py-4 rounded-lg hover:bg-orange-50 transition-colors font-semibold text-lg"
                >
                  Get Free Consultation
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Service Info Card */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Service Information</h3>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <Clock className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <div className="font-medium text-gray-900">Duration</div>
                    <div className="text-gray-600 text-sm">{service.duration}</div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <div className="font-medium text-gray-900">Frequency</div>
                    <div className="text-gray-600 text-sm">{service.frequency}</div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <Shield className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <div className="font-medium text-gray-900">Licensed & Insured</div>
                    <div className="text-gray-600 text-sm">Professional care providers</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Booking Card */}
            <div className="bg-orange-600 rounded-lg p-6 text-white">
              <Heart className="w-8 h-8 mb-4 text-orange-200" />
              <h3 className="text-lg font-bold mb-4">Ready to Get Started?</h3>
              <p className="text-orange-100 mb-6 text-sm">
                Book this service now or contact us for a consultation to learn how we can provide the best care for your loved one.
              </p>

              <div className="space-y-3">
                <Link
                  to={`/elderly-order-request?service=${service.id}&title=${encodeURIComponent(service.name)}&category=elderly-care`}
                  className="block w-full bg-white text-orange-600 text-center py-3 rounded-lg font-semibold hover:bg-orange-50 transition-colors"
                >
                  Book This Service
                </Link>

                <Link
                  to="/contact"
                  className="block w-full border-2 border-white text-white text-center py-3 rounded-lg font-semibold hover:bg-white hover:text-orange-600 transition-colors"
                >
                  Schedule Consultation
                </Link>
              </div>
            </div>

            {/* Related Services */}
            <div className="bg-white rounded-lg shadow-md p-6 mt-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Related Services</h3>
              <div className="space-y-3">
                {Object.values(services)
                  .filter(s => s.id !== service.id && s.category === service.category)
                  .slice(0, 2)
                  .map((relatedService) => {
                    const RelatedIcon = relatedService.icon;
                    return (
                      <Link
                        key={relatedService.id}
                        to={`/elderly-care/${relatedService.id}`}
                        className="flex items-center p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="p-2 bg-orange-100 rounded-lg mr-3">
                          <RelatedIcon className="w-4 h-4 text-orange-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 text-sm">{relatedService.name}</div>
                          <div className="text-gray-600 text-xs">{relatedService.price}</div>
                        </div>
                      </Link>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ElderlyServiceDetailPage;